import { and, eq, not, or } from 'tio-ui/utilities';
import Button from 'tio-reimagined/components/button';
import { concat, fn, hash, array } from '@ember/helper';
import { currencyStringToNumber } from 'tio-common/utils/format';
import { divide } from 'tio-ui/math';
import { htmlSafe } from '@ember/template';
import RadioGroup from 'tio-reimagined/components/radio-group';
import Input from 'tio-reimagined/components/input';
import Textarea from 'tio-reimagined/components/textarea';
import ReSelect from 'tio-reimagined/components/select';
import { inputsForFields } from 'tio-common/utils/data/inputs-for-fields';
import { on } from '@ember/modifier';
import { PhX, PhInfo } from 'ember-phosphor-icons';
import { service } from '@ember/service';
import { t } from 'ember-intl';
import { tracked } from '@glimmer/tracking';
import { trackedArray, trackedObject } from '@ember/reactive/collections';
import EmberFlatpickr from 'ember-flatpickr/components/ember-flatpickr';
import { formatDateToISO } from 'tio-common/utils/date';
import {
  getSingleValueForTasField,
  setSingleValueForTasField,
  getAllValuesForTasField,
  getSortedOptionsByFieldName,
  type TemplateFieldName,
  type FieldName,
  type CustomFieldId,
} from 'tio-common/utils/tuition-assistance/fields';
import Component from '@glimmer/component';
import Drawer from 'tio-reimagined/components/drawer';
import inputmask from 'tio-common/modifiers/inputmask';
import TasCardHolder from 'tio-employee/components/tas/course-cash/card-holder';
import type { CustomFieldSignature } from 'tio-common/types/tuition-assistance';
import type { TASProgramTemplateModelFieldsSignature } from 'tio-data/models/tas-program-template';
import type { TOC } from '@ember/component/template-only';
import type Owner from '@ember/owner';
// This is for types. Anything we have custom may show an error.
import type StoreService from 'ember-data/store';
import type TasApplication from 'tio-data/models/tas-application';
import type TasCourse from 'tio-data/models/tas-course';
import type TasProgramTemplate from 'tio-data/models/tas-program-template';

const number = Number;

interface AddEducationDrawerSignature {
  Args: {
    isOpen: boolean;
    onClose: () => void;
    application: TasApplication;
    course: TasCourse | null;
    header?: string;
    paymentMethod: string;
    setPaymentMethod: (method: string) => void;
  };
  Element: HTMLDivElement;
}

export interface BuiltInput {
  id?: string;
  name: keyof TasCourse['fields'];
  label: string;
  type: string;
  inputType: string;
  required: boolean;
  values: string[];
  errors: string[];
  options?: { key: string; value: string }[];
  selectionMode?: 'single' | 'multiple';
  custom?: boolean;
  visible: boolean;
  ordinal: number;
  province: string;
  rules: { type: 'string' | 'number'; typeError?: 'string' };
  value_as_field_option?: boolean;
}

export interface CustomInput extends CustomFieldSignature {
  custom: boolean;
  name?: string;
}
export interface CourseFieldsHash {
  [key: string]: {
    values: (string | number)[];
  };
}

interface RequiredAsteriskSignature {
  Args: {
    inputField: BuiltInput;
    application: TasApplication;
  };
  Element: HTMLSpanElement;
}

const RequiredAsterisk: TOC<RequiredAsteriskSignature> = <template>
  {{#if
    (or
      (eq @application.typeClassification "TAS.ProgramType.2")
      (eq @application.typeClassification "TAS.ProgramType.4.2")
    )
  }}
    {{#if @inputField.required}}
      <span class="tio-fg-danger ml-0.5">*</span>
    {{/if}}
  {{else if (and @inputField.required (not (eq @inputField.name "COURSE_GRADE")))}}
    <span class="tio-fg-danger ml-0.5">*</span>
  {{/if}}
</template>;

export default class AddEducationDrawerComponent extends Component<AddEducationDrawerSignature> {
  @service declare store: StoreService;
  inputs: BuiltInput[] = trackedArray([]);
  @tracked customInputs: CustomInput[] = [];
  @tracked isLoading: boolean = false;
  @tracked trackedErrors: Record<string, string[]> = {};
  programTemplate: TasProgramTemplate;

  @tracked courseFields: TasCourse['fields'] = {} as TasCourse['fields'];
  @tracked customFields: TasCourse['customFields'] = [];

  createInputsForNewCourse(application: TasApplication): BuiltInput[] {
    const builtInputs = [];
    const programTemplate = application.tasProgramInstance.tasProgramTemplate;

    const courseFieldsHash = structuredClone(programTemplate.courseFieldsHash);
    for (const field in courseFieldsHash) {
      // Merge the field data with input data
      const fieldInput = structuredClone(inputsForFields[field as keyof typeof inputsForFields]);
      const programField = structuredClone(
        programTemplate.fields[field as keyof TASProgramTemplateModelFieldsSignature]
      ) as BuiltInput;

      if (programField.value_as_field_option) {
        const rawOptions = getSortedOptionsByFieldName(
          field as TemplateFieldName,
          programTemplate.tasFieldOptions
        );
        fieldInput.inputType = 'select';
        programField.options = rawOptions.map((opt) => ({
          key: opt.value as string,
          value: opt.value as string,
        }));
      }

      if (
        field === 'COURSE_GRADE' &&
        getAllValuesForTasField('EXCLUSIVE_ACADEMIC_PERFORMANCE_OPTIONS', programTemplate.fields)
          .length
      ) {
        const exclusiveOptions = getAllValuesForTasField(
          'EXCLUSIVE_ACADEMIC_PERFORMANCE_OPTIONS',
          programTemplate.fields
        );
        programField.options = [];
        for (const option of exclusiveOptions) {
          programField.options.push({ key: option as string, value: option as string });
        }
      }
      const mergedInput = Object.assign(fieldInput, programField);

      builtInputs.push(mergedInput);
    }

    return builtInputs.sort((a, b) => a.ordinal - b.ordinal) as BuiltInput[];
  }

  createInputsToEditCourse(application: TasApplication, course: TasCourse): BuiltInput[] {
    const builtInputs = [];
    const programTemplate = application.tasProgramInstance.tasProgramTemplate;

    // Deep clone courseFieldsHash to avoid any unintended mutations
    const courseFieldsHash = structuredClone(programTemplate.courseFieldsHash);

    for (const field in courseFieldsHash) {
      // Clone template input and template field to prevent mutations
      const templateInput = structuredClone(
        inputsForFields[field as keyof typeof inputsForFields] || {}
      );

      const templateField = structuredClone(
        programTemplate.fields[field as keyof typeof programTemplate.fields] || {}
      ) as BuiltInput;

      if (templateField.value_as_field_option) {
        const rawOptions = getSortedOptionsByFieldName(
          field as TemplateFieldName,
          programTemplate.tasFieldOptions
        );
        templateInput.inputType = 'select';
        templateField.options = rawOptions.map((opt) => ({
          key: opt.value as string,
          value: opt.value as string,
        }));
      }

      if (
        field === 'COURSE_GRADE' &&
        getAllValuesForTasField('EXCLUSIVE_ACADEMIC_PERFORMANCE_OPTIONS', programTemplate.fields)
          .length
      ) {
        const exclusiveOptions = getAllValuesForTasField(
          'EXCLUSIVE_ACADEMIC_PERFORMANCE_OPTIONS',
          programTemplate.fields
        );
        templateField.options = [];
        for (const option of exclusiveOptions) {
          templateField.options.push({ key: option as string, value: option as string });
        }
      }

      // Get the corresponding course field if it exists
      const courseField = structuredClone(course.fields[field as keyof typeof course.fields] || {});

      // Merge the course field values into the input, if the course field exists
      const mergedValues = courseField.values ? { values: courseField.values } : {};

      // Merge all inputs, ensuring immutability
      const mergedInput = Object.assign({}, templateInput, templateField, mergedValues);
      builtInputs.push(mergedInput);
    }

    return builtInputs.sort((a, b) => a.ordinal - b.ordinal) as BuiltInput[];
  }

  createCustomInputs(customFields: CustomFieldSignature[]): CustomInput[] {
    return customFields.map((customField) => ({
      ...customField,
      custom: true,
    }));
  }

  createErrorsObject(
    courseFieldsHash: CourseFieldsHash[],
    customFields: CustomFieldSignature[]
  ): Record<string, string[]> {
    const errors: Record<string, string[]> = {};
    for (const field in courseFieldsHash) {
      errors[field] = [];
    }
    for (const field of customFields) {
      errors[field.label] = [];
    }
    return errors;
  }

  constructor(owner: Owner, args: AddEducationDrawerSignature['Args']) {
    super(owner, args);
    const { application, course } = args;

    this.inputs = course!.isNew
      ? trackedArray(this.createInputsForNewCourse(application))
      : trackedArray(this.createInputsToEditCourse(application, course!));
    this.courseFields = trackedObject(course!.fields);
    this.customFields = trackedArray(course!.customFields);
    this.customInputs = trackedArray(this.createCustomInputs(course!.customFields));

    this.programTemplate = application.tasProgramInstance.tasProgramTemplate;

    this.trackedErrors = trackedObject(
      this.createErrorsObject(this.programTemplate.courseFieldsHash, course!.customFields)
    );
  }

  inputClasses = { base: 'w-full mb-6 p-1' };

  getErrorForInput(
    input: BuiltInput | CustomInput,
    trackedErrors: Record<string, string[]>
  ): string[] {
    if (input.custom) {
      return trackedErrors[input.label] || [];
    } else {
      return trackedErrors[input.name!] || [];
    }
  }

  // This function is required for type three applications to by pass grade requirement here
  checkForRequired(input: BuiltInput) {
    if (
      input.name === 'COURSE_GRADE' &&
      this.args.application.typeClassification !== 'TAS.ProgramType.2' &&
      this.args.application.typeClassification !== 'TAS.ProgramType.4.2'
    ) {
      return false;
    }
    if (input.required) {
      return true;
    }
    return false;
  }

  checkForErrors = () => {
    for (const input of this.inputs) {
      // Check if the input is required
      if (this.checkForRequired(input)) {
        if (input.values.length === 0) {
          this.trackedErrors[input.name] = ['This field is required'];
        } else {
          delete this.trackedErrors[input.name];
        }
      }

      // Check for correct type (if there are values)
      if (input.values.length > 0) {
        const value = input.values[0];

        // If the expected type is "number"
        if (input.type === 'number') {
          if (isNaN(Number(value))) {
            this.trackedErrors[input.name] = ['Value must be a number'];
          } else {
            delete this.trackedErrors[input.name];
          }
        }

        // If the expected type is "string"
        if (input.type === 'string') {
          if (typeof value !== 'string') {
            this.trackedErrors[input.name] = ['Value must be a string'];
          } else {
            delete this.trackedErrors[input.name];
          }
        }
      }
    }
  };

  checkForCustomInputErrors = () => {
    for (const input of this.customInputs) {
      // check if required
      if (input.required) {
        if (input.values.length === 0) {
          this.trackedErrors[input.label] = ['This field is required'];
        } else {
          delete this.trackedErrors[input.label];
        }
      }
    }
  };

  noErrors = (): boolean => {
    for (const key in this.trackedErrors) {
      if (Object.prototype.hasOwnProperty.call(this.trackedErrors, key)) {
        if (this.trackedErrors[key]?.length !== 0) {
          return false;
        }
      }
    }
    return true;
  };

  addEducation = async () => {
    this.checkForErrors();
    this.checkForCustomInputErrors();
    if (this.noErrors()) {
      this.isLoading = true;
      try {
        this.setApprovedCourseTotalBeforeSave();
        this.args.course!.fields = this.courseFields;
        this.args.course!.customFields = this.customFields;
        await this.args.course!.save();
        this.inputs = trackedArray([]);
        this.customInputs = [];
        this.courseFields = {} as TasCourse['fields'];
        this.customFields = [];
        this.args.onClose();
      } catch (error) {
        console.error(error);
      }
      this.isLoading = false;
    }
  };

  getValue = <T extends string | number | boolean | undefined>(
    values: (string | number | boolean)[]
  ): T | undefined => {
    const firstValue = values[0];
    if (firstValue === '' || firstValue === undefined) {
      return undefined as T;
    }
    return firstValue as T;
  };

  getStringValue = (values: (string | number | boolean)[]): string | undefined => {
    const val = this.getValue(values);
    if (val === undefined) return undefined;
    return String(val);
  };

  boolify(value: string): boolean {
    return !!value;
  }

  setCustomDateValue = (customInput: CustomInput, selectedDates: Date[]) => {
    if (selectedDates.length > 0 && selectedDates[0]) {
      const isoDate = formatDateToISO(selectedDates[0]);
      this.setValue(customInput as unknown as BuiltInput, isoDate);
    }
  };

  setCustomSelectValue = (customInput: CustomInput, key: string | null) => {
    if (key !== null) {
      this.setValue(customInput as unknown as BuiltInput, key);
    }
  };

  getRadioOptionsForCustomInput = (customInput: CustomInput) => {
    return (customInput.options || []).map((opt) => ({
      key: String(opt.value),
      label: String(opt.label),
    }));
  };

  getSelectOptionsForCustomInput = (customInput: CustomInput) => {
    if (customInput.value_as_field_option && customInput.id) {
      return getSortedOptionsByFieldName(
        customInput.id as CustomFieldId,
        this.programTemplate.tasFieldOptions
      ).map((opt) => ({ key: String(opt.value), value: String(opt.value) }));
    }
    return (customInput.options || []).map((opt) => ({
      key: String(opt.value),
      value: String(opt.label),
    }));
  };

  get courseExpenses() {
    const expenseInputs: FieldName[] = [
      'COURSE_TUITION',
      'COURSE_BOOKS',
      'COURSE_LABS',
      'COURSE_SOFTWARE',
      'COURSE_FEES',
    ];

    const reduced = expenseInputs.reduce((acc, input) => {
      const value = getSingleValueForTasField(input, this.courseFields) as number;
      return acc + (isNaN(value) ? 0 : value);
    }, 0);

    return reduced;
  }

  setApprovedCourseTotalBeforeSave() {
    setSingleValueForTasField('APPROVED_COURSE_TOTAL', this.courseTotal, this.courseFields);
  }

  get courseTotal() {
    return this.programTemplate.calculateTotalByCredit
      ? this.reimbursementTotalByCredit
      : this.courseExpenses;
  }

  get reimbursementTotalByCredit() {
    return (
      (this.programTemplate.ratePerCredit ?? 0) *
      ((getSingleValueForTasField('COURSE_CREDIT', this.courseFields) as number) ?? 0)
    );
  }

  setValue = (input: BuiltInput, value: string | number | boolean | string[]) => {
    // Handle currency formatting for both standard fields (type) and custom fields (inputType)
    if (input.type === 'currency' || input.inputType === 'currency') {
      value = currencyStringToNumber(value as string);
    }
    if (input.type === 'number') {
      // course credits can be halves ex: 2.5
      value = parseFloat(value as string);
      if (isNaN(value)) {
        this.trackedErrors[input.name] = ['This field must be a number'];
      } else {
        delete this.trackedErrors[input.name];
      }
    }
    if (Array.isArray(value)) {
      // For multi-select, store the array directly to avoid nested arrays
      input.values = value;
    } else {
      // @ts-expect-error: boolean not assignable to string | number
      input.values = [value];
    }

    // Only splice (replace object identity) for select-type inputs.
    // ReSelect is fully controlled (@selected), so it needs a new object identity to re-render.
    // For textarea/input, splicing destroys and recreates the DOM element on every keystroke,
    // which loses focus. Plain mutation (above) is enough since the browser already shows the
    // typed value in the DOM.
    if (input.inputType === 'select') {
      const updatedInput = { ...input };
      const idx = this.inputs.findIndex(
        (existingInput) =>
          existingInput.name === input.name && existingInput.ordinal === input.ordinal
      );
      if (idx !== -1) {
        this.inputs.splice(idx, 1, updatedInput);
      }
    }

    if (input.custom) {
      const index = input.ordinal - 1;
      if (Array.isArray(value)) {
        this.customFields[index] = {
          ...this.customFields[index],
          values: value,
        } as CustomFieldSignature;
      } else {
        this.customFields[index] = {
          ...this.customFields[index],
          values: [value] as string[] | boolean[],
        } as CustomFieldSignature;
      }

      // Splice customInputs for select-type and radio-type custom inputs.
      // Both ReSelect and RadioGroup are fully controlled components that need a new object
      // identity to re-render and show the updated selection state immediately.
      // For textarea/input, splicing causes the DOM element to be destroyed and recreated
      // on every keystroke, which loses focus.
      const customIdx = this.customInputs.findIndex((ci) => ci.ordinal === input.ordinal);
      if (customIdx !== -1) {
        const customInputType = this.customInputs[customIdx]?.input;
        if (customInputType === 'select' || customInputType === 'radio') {
          const existing = this.customInputs[customIdx]!;
          this.customInputs.splice(customIdx, 1, {
            ...existing,
            custom: existing.custom ?? true,
            values: input.values,
          });
        }
      }
    } else {
      if (Array.isArray(value)) {
        // For arrays, we might need to use a different method or handle differently
        // For now, take the first value for single-value fields
        setSingleValueForTasField(input.name, value[0] || '', this.courseFields);
      } else {
        setSingleValueForTasField(input.name, value, this.courseFields);
      }
    }
  };

  get courseInformationInputs(): BuiltInput[] {
    return (this.inputs || []).filter((input) =>
      [
        'COURSE_NAME',
        'COURSE_NUMBER',
        'COURSE_DESCRIPTION',
        'COURSE_CREDIT',
        'COST_PER_CREDIT',
      ].includes(input.name as string)
    );
  }

  get tuitionInputs(): BuiltInput[] {
    return (this.inputs || []).filter((input) => input.name === 'COURSE_TUITION');
  }

  get otherFeesInputs(): BuiltInput[] {
    return (this.inputs || []).filter((input) =>
      ['COURSE_BOOKS', 'COURSE_LABS', 'COURSE_FEES', 'COURSE_SOFTWARE'].includes(
        input.name as string
      )
    );
  }

  get gradeInputs(): BuiltInput[] {
    return (this.inputs || []).filter((input) => input.name === 'COURSE_GRADE');
  }

  hasAnyVisibleInput(inputs: BuiltInput[]): boolean {
    if (!inputs || !Array.isArray(inputs)) {
      return false;
    }
    return inputs.some((input) => input.visible);
  }

  get isDirectPayProgram() {
    return this.programTemplate.isDirectPayProgram;
  }

  get addCourseDisabled() {
    return (
      this.isDirectPayProgram &&
      this.args.paymentMethod === 'TAS.AssistanceManner.COURSE_CASH' &&
      this.args.application.tasProgramInstance.employee.pcsCardHolders.length === 0
    );
  }

  updatePaymentMethod = (selected: string) => {
    this.args.setPaymentMethod(selected);
  };

  <template>
    <Drawer
      @isOpen={{@isOpen}}
      @onClose={{@onClose}}
      @allowCloseButton={{false}}
      @size="xl"
      @placement="right"
      @closeOnOutsideClick={{false}}
      @closeOnEscapeKey={{true}}
    >
      <:header>
        <div class="tio-row-between">
          <div class="flex-1">
            <h2 class="tio-fg-secondary font-semibold">
              {{or @header (t "tas.add_education_drawer.education_information")}}
            </h2>
            <p class="tio-fg-disabled text-sm">
              {{t "tas.add_education_drawer.fill_out_education_below"}}
            </p>
            <p class="tio-fg-disabled text-sm">
              {{t "tas.add_education_drawer.required_fields"}}
              <span class="text-error-400">*</span>
            </p>
          </div>
          <button
            type="button"
            data-test-close-education-drawer
            {{on "click" @onClose}}
            class="ml-4"
          >
            <PhX @size="24" />
          </button>
        </div>
      </:header>

      <:body>
        <div class="flex-1 overflow-y-auto">
          {{! ───────── Course Information Section ───────── }}
          {{#if (this.hasAnyVisibleInput this.courseInformationInputs)}}
            <div class="border-b tio-border-default pb-4">
              <div class="py-2">
                <h3 class="tio-fg-secondary text-lg">{{t
                    "tas.add_education_drawer.course_information"
                  }}</h3>
              </div>
              {{#each this.courseInformationInputs as |inputData|}}
                {{#if inputData.visible}}
                  <div class="tio-col-flush py-2 w-full">
                    <InputFormField
                      @inputData={{inputData}}
                      @errors={{this.getErrorForInput inputData this.trackedErrors}}
                      @onChange={{fn this.setValue inputData}}
                      @inputClasses={{this.inputClasses}}
                      @application={{@application}}
                    />
                  </div>
                {{/if}}
              {{/each}}
            </div>
          {{/if}}

          {{! ───────── Tuition Section ───────── }}
          {{#if (this.hasAnyVisibleInput this.tuitionInputs)}}
            <div class="border-b tio-border-default pb-4">
              <div class="pt-6 pb-2">
                <h3 class="tio-fg-secondary text-lg">{{t "tas.add_education_drawer.tuition"}}</h3>
              </div>
              {{#each this.tuitionInputs as |inputData|}}
                {{#if inputData.visible}}
                  <div class="tio-col-flush py-2 w-full tuition-input">
                    <InputFormField
                      @inputData={{inputData}}
                      @errors={{this.getErrorForInput inputData this.trackedErrors}}
                      @onChange={{fn this.setValue inputData}}
                      @inputClasses={{this.inputClasses}}
                      @application={{@application}}
                    />
                  </div>

                  {{#if this.isDirectPayProgram}}
                    <div class="-mt-2 mb-6">
                      <div class="font-semibold tio-fg-secondary">
                        {{t "tas.add_education_drawer.payment_method"}}
                        <span class="text-error-400">*</span>
                      </div>
                      <p class="text-sm mb-4 text-tio-gray-600">
                        {{htmlSafe (t "tas.add_education_drawer.select_payment_method")}}
                      </p>
                      <RadioGroup
                        @value={{@paymentMethod}}
                        @onChange={{this.updatePaymentMethod}}
                        @orientation="vertical"
                        @isRequired={{true}}
                        @options={{array
                          (hash
                            key="TAS.AssistanceManner.COURSE_CASH"
                            label=(t "tas.payment_method.course_cash_payment_method")
                            dataTestId="radio-course-cash"
                          )
                          (hash
                            key="TAS.AssistanceManner.REIMBURSEMENT"
                            label=(t "tas.payment_method.reimbursement_payment_method")
                            dataTestId="radio-reimbursement"
                          )
                        }}
                      />
                      {{#if (eq @paymentMethod "TAS.AssistanceManner.COURSE_CASH")}}
                        <div class="px-8">
                          {{#if @application.tasProgramInstance.employee.pcsCardHolders.length}}
                            <div class="tio-row rounded-2xl tio-bg-sunken p-4 text-sm">
                              <div class="shrink-0">
                                <PhInfo @size="24" class="tio-fg-muted" />
                              </div>
                              <div class="flex-1 tio-fg-secondary">
                                {{htmlSafe (t "tas.add_education_drawer.course_cash_selected")}}
                              </div>
                            </div>
                          {{else}}
                            <TasCardHolder @application={{@application}} />
                          {{/if}}
                        </div>
                      {{/if}}
                      {{#if (eq @paymentMethod "TAS.AssistanceManner.REIMBURSEMENT")}}
                        <div class="px-8">
                          <div class="tio-row rounded-2xl tio-bg-sunken p-4 text-sm">
                            <div class="shrink-0">
                              <PhInfo @size="24" class="tio-fg-muted" />
                            </div>
                            <div class="flex-1 tio-fg-secondary">
                              {{t "tas.add_education_drawer.reimbursement_selected"}}
                            </div>
                          </div>
                        </div>
                      {{/if}}
                    </div>
                  {{/if}}
                {{/if}}
              {{/each}}
            </div>
          {{/if}}

          {{! ───────── Other Fees Section ───────── }}
          {{#if (this.hasAnyVisibleInput this.otherFeesInputs)}}
            <div class="border-b tio-border-default pb-4">
              <div class="pt-6 pb-2">
                <h3 class="tio-fg-secondary text-lg">{{t
                    "tas.add_education_drawer.other_fees"
                  }}</h3>
                {{#if this.isDirectPayProgram}}
                  <p class="text-sm mb-4 text-tio-gray-600">
                    {{htmlSafe
                      (t
                        "tas.add_education_drawer.not_eligible_for_course_cash"
                        courseCash=(t "common.tas.course_cash.default")
                      )
                    }}
                  </p>
                {{/if}}
              </div>
              {{#each this.otherFeesInputs as |inputData|}}
                {{#if inputData.visible}}
                  <div class="tio-col-flush py-2 w-full">
                    <InputFormField
                      @inputData={{inputData}}
                      @errors={{this.getErrorForInput inputData this.trackedErrors}}
                      @onChange={{fn this.setValue inputData}}
                      @inputClasses={{this.inputClasses}}
                      @application={{@application}}
                    />
                  </div>
                {{/if}}
              {{/each}}
            </div>
          {{/if}}

          {{! ───────── Grade Section ─────────}}
          {{#if (this.hasAnyVisibleInput this.gradeInputs)}}
            <div class="border-b tio-border-default pb-4">
              <div class="pt-6 pb-2">
                <h3 class="tio-fg-secondary text-lg">{{t "tas.add_education_drawer.grade"}}</h3>
              </div>
              {{#each this.gradeInputs as |inputData|}}
                {{#if inputData.visible}}
                  <div class="tio-col-flush py-2 w-full">
                    <InputFormField
                      @inputData={{inputData}}
                      @errors={{this.getErrorForInput inputData this.trackedErrors}}
                      @onChange={{fn this.setValue inputData}}
                      @inputClasses={{this.inputClasses}}
                      @application={{@application}}
                    />
                  </div>
                {{/if}}
              {{/each}}
            </div>
          {{/if}}

          {{! ───────── Custom Inputs ───────── }}
          {{#each this.customInputs key="ordinal" as |customInput index|}}
            {{#if customInput.visible}}
              <div class="tio-col-flush py-4 w-full border-b tio-border-default">
                <label class="font-semibold tio-fg-secondary" for={{customInput.name}}>
                  {{customInput.label}}
                  {{#if customInput.required}}
                    <span class="text-error-400">*</span>
                  {{/if}}
                </label>

                {{! ───────── Input of type="currency" ───────── }}
                {{#if (and (eq customInput.input "input") (eq customInput.inputType "currency"))}}
                  <Input
                    @value={{divide (or (number (this.getValue customInput.values)) 0) 100}}
                    {{! @glint-expect-error }}
                    @onChange={{fn this.setValue customInput}}
                    {{inputmask
                      alias="currency"
                      prefix="$"
                      unmaskAsNumber=true
                      digits="2"
                      autoUnmask=true
                    }}
                    @errors={{this.getErrorForInput customInput this.trackedErrors}}
                    data-test={{concat "custom-input-currency-" index}}
                  />

                  {{! ───────── Input of type="date" ───────── }}
                {{else if (and (eq customInput.input "input") (eq customInput.inputType "date"))}}
                  <EmberFlatpickr
                    {{! @glint-expect-error: getValue may return boolean but will be string for date fields}}
                    @date={{or (this.getValue customInput.values) ""}}
                    @onChange={{fn this.setCustomDateValue customInput}}
                    @dateFormat="m/d/Y"
                    @disableMobile={{true}}
                    @position="below left"
                    placeholder="mm/dd/yyyy"
                    data-test={{concat "custom-input-date-" index}}
                    class="tio-form-input-lg"
                  />

                  {{! ───────── Input of type="string" ───────── }}
                {{else if (eq customInput.input "input")}}
                  <Input
                    @value={{this.getValue customInput.values}}
                    {{! @glint-expect-error }}
                    @onChange={{fn this.setValue customInput}}
                    @errors={{this.getErrorForInput customInput this.trackedErrors}}
                    data-test={{concat "custom-input-text-" index}}
                  />

                  {{! ───────── Textarea ───────── }}
                {{else if (eq customInput.input "textarea")}}
                  <Textarea
                    @value={{this.getValue customInput.values}}
                    {{! @glint-expect-error }}
                    @onChange={{fn this.setValue customInput}}
                    @errors={{this.getErrorForInput customInput this.trackedErrors}}
                    data-test={{concat "custom-input-textarea-" index}}
                  />

                  {{! ───────── Radio Group ───────── }}
                {{else if (eq customInput.input "radio")}}
                  <RadioGroup
                    @value={{this.getValue customInput.values}}
                    {{! @glint-expect-error }}
                    @onChange={{fn this.setValue customInput}}
                    @errors={{this.getErrorForInput customInput this.trackedErrors}}
                    data-test={{concat "custom-input-radio-" index}}
                    @options={{this.getRadioOptionsForCustomInput customInput}}
                  />

                  {{! ───────── Select ───────── }}
                {{else if (eq customInput.input "select")}}
                  <ReSelect
                    @selected={{this.getStringValue customInput.values}}
                    @items={{this.getSelectOptionsForCustomInput customInput}}
                    @onChange={{fn this.setCustomSelectValue customInput}}
                    @errors={{this.getErrorForInput customInput this.trackedErrors}}
                    @placeholder="Select an option"
                    @clearable={{false}}
                    data-test={{concat "custom-input-select-" index}}
                  />
                {{/if}}
              </div>
            {{/if}}
          {{/each}}
        </div>
      </:body>

      <:footer>
        <div class="flex justify-start items-center gap-4 w-full">
          <Button @appearance="minimal" data-test-cancel-education-drawer {{on "click" @onClose}}>
            {{t "common.cancel"}}
          </Button>
          <Button
            type="submit"
            @intent="primary"
            @isRunning={{this.isLoading}}
            data-test-save-education-drawer
            {{on "click" this.addEducation}}
            @isDisabled={{this.addCourseDisabled}}
          >
            {{if @course.isNew (t "common.add") (t "common.save")}}
          </Button>
        </div>
      </:footer>
    </Drawer>
  </template>
}

interface InputFormFieldSignature {
  Args: {
    inputData: BuiltInput;
    errors: string[];
    onChange: (value: string | number | boolean | string[]) => void;
    inputClasses: Record<string, string>;
    application: TasApplication;
  };
  Element: HTMLDivElement;
}

class InputFormField extends Component<InputFormFieldSignature> {
  handleChange = (newValue: string | number | boolean) => {
    this.args.onChange(newValue);
  };

  handleSelectChange = (key: string | null) => {
    if (key !== null) {
      this.args.onChange(key);
    }
  };

  handleMultipleChange = (newValues: string[]) => {
    this.args.onChange(newValues);
  };

  getSelectedValue = (values: (string | number | boolean)[]): string | undefined => {
    const firstValue = values[0];
    if (firstValue === '' || firstValue === undefined) {
      return undefined;
    }
    return String(firstValue);
  };

  getValue = <T extends string | number | boolean | undefined>(
    values: (string | number | boolean)[]
  ): T | undefined => {
    const firstValue = values[0];
    if (firstValue === '' || firstValue === undefined) {
      return undefined as T;
    }
    return firstValue as T;
  };

  <template>
    <label for={{@inputData.name}} class="font-semibold tio-fg-secondary">
      {{@inputData.label}}
      {{! Show the “required *” only if the parent says so: }}
      <RequiredAsterisk @inputField={{@inputData}} @application={{@application}} />
    </label>

    {{! ───────── Input of type="currency" ───────── }}
    {{#if (and (eq @inputData.inputType "input") (eq @inputData.type "currency"))}}
      <Input
        id={{@inputData.name}}
        data-test={{@inputData.name}}
        @value={{divide (or (number (this.getValue @inputData.values)) 0) 100}}
        {{! When the user blurs/changes, call our local handler: }}
        @onChange={{this.handleChange}}
        {{inputmask alias="currency" prefix="$" unmaskAsNumber=true digits="2" autoUnmask=true}}
        @errors={{@errors}}
        data-test-education-drawer-input-currency
      />

      {{! ───────── Input of type="string" ───────── }}
    {{else if (and (eq @inputData.inputType "input") (eq @inputData.type "string"))}}
      <Input
        id={{@inputData.name}}
        data-test={{@inputData.name}}
        @value={{this.getValue @inputData.values}}
        @onChange={{this.handleChange}}
        @errors={{@errors}}
      />

      {{! ───────── Input of type="number" ───────── }}
    {{else if (and (eq @inputData.inputType "input") (eq @inputData.type "number"))}}
      <Input
        id={{@inputData.name}}
        data-test={{@inputData.name}}
        @value={{this.getValue @inputData.values}}
        @onChange={{this.handleChange}}
        @errors={{@errors}}
      />

      {{! ───────── Textarea ───────── }}
    {{else if (eq @inputData.inputType "textarea")}}
      <Textarea
        id={{@inputData.name}}
        data-test={{@inputData.name}}
        @value={{this.getValue @inputData.values}}
        @onChange={{this.handleChange}}
        @errors={{@errors}}
      />
      {{! ───────── Radio Group ───────── }}
      {{! Temporarily removed bc not used in TAS yet for standard fields}}
      {{!-- {{else if (eq @inputData.inputType "radio")}}
      <RadioGroup
        id={{@inputData.name}}
        data-test={{@inputData.name}}
        @value={{this.getValue @inputData.values}}
        @onChange={{this.handleChange}}
        @classes={{hash base="py-4"}}
        as |Radio|
      >
        {{#each @inputData.options as |option|}}
          <Radio @value={{option.value}} @label={{option.label}} />
        {{/each}}
      </RadioGroup> --}}

      {{! ───────── Select dropdown ───────── }}
    {{else if (eq @inputData.inputType "select")}}
      {{#if (eq @inputData.selectionMode "multiple")}}
        <ReSelect
          id={{@inputData.name}}
          data-test={{@inputData.name}}
          @items={{or @inputData.options (array)}}
          @multiple={{true}}
          @selectedKeys={{@inputData.values}}
          @onChangeKeys={{this.handleMultipleChange}}
          @errors={{@errors}}
        />
      {{else}}
        <ReSelect
          id={{@inputData.name}}
          data-test={{@inputData.name}}
          @items={{or @inputData.options (array)}}
          @selected={{this.getSelectedValue @inputData.values}}
          @onChange={{this.handleSelectChange}}
          @errors={{@errors}}
          @clearable={{false}}
          @placeholder="Select an option"
        />
      {{/if}}
    {{/if}}
  </template>
}
